// MCHD Room Scheduler - Coordinator Session Time Form Component
// Form for creating and editing room session times (coordinator role)

import React, { useEffect, useMemo } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Select,
  NumberInput,
  Switch,
  Group,
  Button,
  Text,
  Alert,
  Divider,
  Title,
  Badge,
  Grid,
  Card,
  ThemeIcon,
} from '@mantine/core';
import {
  IconClock,
  IconBuilding,
  IconDoor,
  IconCheck,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';

// Import hooks
import { useRoomSessionTimeById } from '../../../hooks/admin/useSessionTimes';

// Import types
import type {
  RoomSessionTimeWithDetails,
  CreateRoomSessionTimeData,
  Building,
  Room,
} from '../../../api/endpoints/session-times';

// =============================================================================
// INTERFACES
// =============================================================================

interface SessionTimeFormProps {
  sessionTime?: RoomSessionTimeWithDetails | null;
  buildings: Building[];
  rooms: Room[];
  onSubmit: (data: CreateRoomSessionTimeData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Coordinator Session Time Form Component
 * Provides form interface for creating and editing session times
 */
export function SessionTimeForm({
  sessionTime,
  buildings,
  rooms,
  onSubmit,
  onCancel,
  isLoading = false,
}: SessionTimeFormProps): JSX.Element {
  // =============================================================================
  // HOOKS
  // =============================================================================

  // Get session time data for editing
  const { data: sessionTimeData } = useRoomSessionTimeById(
    sessionTime?.id || '',
    !!sessionTime?.id
  );

  // =============================================================================
  // FORM STATE
  // =============================================================================

  const [formData, setFormData] = React.useState<CreateRoomSessionTimeData>({
    name: '',
    startTime: '',
    endTime: '',
    bufferMinutes: 0,
    roomId: '',
    isActive: true,
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  // Memoized room options for Select component with proper Mantine grouped structure
  const roomOptions = useMemo(() => {
    try {
      console.log('SessionTimeForm - Processing room options:', { rooms, buildings });
      
      // Ensure rooms is always an array
      const safeRooms = Array.isArray(rooms) ? rooms : [];
      
      console.log('SessionTimeForm - Safe rooms data:', { count: safeRooms.length });
      
      if (safeRooms.length === 0) {
        console.log('SessionTimeForm - No rooms available');
        return [];
      }
      
      // Track seen IDs to prevent duplicates
      const seenIds = new Set<string>();
      const duplicateIds = new Set<string>();
      
      // Group rooms by building for proper Mantine Select grouped data structure
      const groupedByBuilding = safeRooms
        .filter(room => {
          // Validate room structure
          if (!room || typeof room !== 'object') {
            console.warn('SessionTimeForm - Invalid room object:', room);
            return false;
          }
          
          if (!room.id || !room.name || !room.number) {
            console.warn('SessionTimeForm - Room missing required fields:', room);
            return false;
          }
          
          // Check for duplicate IDs
          if (seenIds.has(room.id)) {
            duplicateIds.add(room.id);
            console.warn('SessionTimeForm - Duplicate room ID detected:', room.id);
            return false; // Skip duplicate
          }
          
          seenIds.add(room.id);
          return true;
        })
        .reduce((acc, room) => {
          // Extract building name from nested structure with defensive checks
          let buildingName = 'Unknown Building';
          
          try {
            if (room.floor?.building?.name) {
              buildingName = room.floor.building.name;
            }
          } catch (err) {
            console.warn('SessionTimeForm - Error extracting building name for room:', room.id, err);
          }
          
          if (!acc[buildingName]) {
            acc[buildingName] = [];
          }
          
          acc[buildingName].push({
            value: String(room.id),
            label: `${room.name} (${room.number})`
          });
          
          return acc;
        }, {} as Record<string, Array<{ value: string; label: string }>>);
      
      // Log duplicates if found
      if (duplicateIds.size > 0) {
        console.error('SessionTimeForm - Found duplicate room IDs:', Array.from(duplicateIds));
      }
      
      // Convert to Mantine's expected grouped data structure
      const mantineGroupedData = Object.entries(groupedByBuilding).map(([building, items]) => ({
        group: building,
        items: items
      }));
      
      console.log('SessionTimeForm - Processed rooms successfully:', mantineGroupedData.length, 'groups');
      return mantineGroupedData;
    } catch (error) {
      console.error('SessionTimeForm - Error processing rooms data for Select:', error);
      return [];
    }
  }, [rooms, buildings]);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Load session time data when editing
  useEffect(() => {
    if (sessionTimeData) {
      const data = sessionTimeData;
      setFormData({
        name: data.name,
        startTime: data.startTime,
        endTime: data.endTime,
        bufferMinutes: data.bufferMinutes,
        roomId: data.roomId,
        isActive: data.isActive,
      });
    } else if (sessionTime) {
      setFormData({
        name: sessionTime.name,
        startTime: sessionTime.startTime,
        endTime: sessionTime.endTime,
        bufferMinutes: sessionTime.bufferMinutes,
        roomId: sessionTime.roomId,
        isActive: sessionTime.isActive,
      });
    }
  }, [sessionTimeData, sessionTime]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field: keyof CreateRoomSessionTimeData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData['name']?.trim()) {
      newErrors['name'] = 'Session name is required';
    }

    if (!formData['startTime']) {
      newErrors['startTime'] = 'Start time is required';
    }

    if (!formData['endTime']) {
      newErrors['endTime'] = 'End time is required';
    }

    if (formData['startTime'] && formData['endTime'] && formData['startTime'] >= formData['endTime']) {
      newErrors['endTime'] = 'End time must be after start time';
    }

    if (!formData['roomId']) {
      newErrors['roomId'] = 'Room selection is required';
    }

    if ((formData['bufferMinutes'] ?? 0) < 0) {
      newErrors['bufferMinutes'] = 'Buffer minutes cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    setFormData({
      name: '',
      startTime: '',
      endTime: '',
      bufferMinutes: 0,
      roomId: '',
      isActive: true,
    });
    setErrors({});
  };

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const isEditing = !!sessionTime;
  const selectedRoom = rooms.find(room => room.id === formData.roomId);
  
  // Safely extract building information from selected room
  const selectedBuilding = selectedRoom?.floor?.building || null;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>
              {isEditing ? 'Edit Session Time' : 'Create New Session Time'}
            </Title>
            <Text c="dimmed" size="sm">
              {isEditing ? 'Update session time details' : 'Configure a new room session time'}
            </Text>
          </div>
          {isEditing && (
            <Badge color="blue" variant="light">
              Editing
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {/* Session Details */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="blue" variant="light">
                  <IconClock size={16} />
                </ThemeIcon>
                <Text fw={500}>Session Details</Text>
              </Group>

              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    label="Session Name"
                    placeholder="Enter session name (e.g., Morning Session, Surgery Block)"
                    value={formData['name'] || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={errors['name']}
                    required
                    leftSection={<IconClock size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Start Time"
                    type="time"
                    value={formData['startTime'] || ''}
                    onChange={(e) => handleFieldChange('startTime', e.target.value)}
                    error={errors['startTime']}
                    required
                    leftSection={<IconClock size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="End Time"
                    type="time"
                    value={formData['endTime'] || ''}
                    onChange={(e) => handleFieldChange('endTime', e.target.value)}
                    error={errors['endTime']}
                    required
                    leftSection={<IconClock size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <NumberInput
                    label="Buffer Minutes"
                    placeholder="Minutes between sessions"
                    value={formData['bufferMinutes'] ?? 0}
                    onChange={(value) => handleFieldChange('bufferMinutes', value || 0)}
                    error={errors['bufferMinutes']}
                    min={0}
                    max={60}
                    leftSection={<IconClock size={16} />}
                    description="Time buffer between this session and the next"
                  />
                </Grid.Col>
              </Grid>
            </Card>

            {/* Room Selection */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="green" variant="light">
                  <IconDoor size={16} />
                </ThemeIcon>
                <Text fw={500}>Room Assignment</Text>
              </Group>

              <Grid>
                <Grid.Col span={12}>
                  <Select
                    label="Room"
                    placeholder="Select a room for this session"
                    value={formData['roomId'] || ''}
                    onChange={(value) => handleFieldChange('roomId', value || '')}
                    error={errors['roomId']}
                    required
                    leftSection={<IconDoor size={16} />}
                    data={roomOptions}
                    searchable
                    clearable
                  />
                </Grid.Col>
                {selectedRoom && selectedBuilding && (
                  <Grid.Col span={12}>
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                      <Text size="sm">
                        <strong>Selected:</strong> {selectedRoom.name} ({selectedRoom.number}) in {selectedBuilding.name}
                        <br />
                        <strong>Capacity:</strong> {selectedRoom.capacity} people
                      </Text>
                    </Alert>
                  </Grid.Col>
                )}
              </Grid>
            </Card>

            {/* Status */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="orange" variant="light">
                  <IconBuilding size={16} />
                </ThemeIcon>
                <Text fw={500}>Status</Text>
              </Group>

              <Switch
                label="Active Session"
                description="Enable this session time for scheduling"
                checked={formData['isActive'] ?? true}
                onChange={(e) => handleFieldChange('isActive', e.currentTarget.checked)}
                color="green"
                size="md"
              />
            </Card>

            {/* Actions */}
            <Group justify="flex-end" mt="lg">
              <Button
                variant="outline"
                onClick={onCancel}
                leftSection={<IconX size={16} />}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                leftSection={<IconX size={16} />}
                disabled={isLoading}
              >
                Reset
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                leftSection={<IconCheck size={16} />}
              >
                {isEditing ? 'Update Session Time' : 'Create Session Time'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}

export default SessionTimeForm;
